import { DOCUMENT, ErrorHandler, Injectable, inject } from '@angular/core';
import { ErrorLoggerService } from '../services/error-logger.service';
import { ChartRecoveryService } from './chart-recovery.service';

// Chrome, Firefox and Safari wording for a lazy chunk the server no longer has.
const STALE_CHUNK_MESSAGE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/;
export const STALE_CHUNK_RELOAD_STORAGE_KEY = 'ctat.staleChunkReloadAt';
export const STALE_CHUNK_RELOAD_COOLDOWN_MS = 10_000;

// V8/WebKit and SpiderMonkey wording when @swimlane/ngx-charts exhausts its module-scoped ID cache.
// Cooldown exceeds the 60s Dashboard poll interval so a bug on every poll cannot reload every minute.
const STACK_OVERFLOW_MESSAGE = /Maximum call stack size exceeded|too much recursion/i;
export const STACK_OVERFLOW_RELOAD_STORAGE_KEY = 'ctat.stackOverflowReloadAt';
export const STACK_OVERFLOW_RELOAD_COOLDOWN_MS = 300_000;

function extractMessage(error: unknown): unknown {
  const candidate = (error as { rejection?: unknown })?.rejection ?? error;
  return (candidate as { message?: unknown })?.message;
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly errorLogger = inject(ErrorLoggerService);
  private readonly chartRecovery = inject(ChartRecoveryService);
  private readonly document = inject(DOCUMENT);

  handleError(error: unknown): void {
    if (!this.recoverByReload(extractMessage(error))) {
      this.errorLogger.logError(error);
    }
  }

  private recoverByReload(message: unknown): boolean {
    if (typeof message !== 'string') {
      return false;
    }
    if (STALE_CHUNK_MESSAGE.test(message)) {
      return this.reloadOnce(STALE_CHUNK_RELOAD_STORAGE_KEY, STALE_CHUNK_RELOAD_COOLDOWN_MS);
    }
    if (!STACK_OVERFLOW_MESSAGE.test(message)) {
      return false;
    }
    this.chartRecovery.markFailed();
    return this.reloadOnce(STACK_OVERFLOW_RELOAD_STORAGE_KEY, STACK_OVERFLOW_RELOAD_COOLDOWN_MS);
  }

  /**
   * Reloads the page at most once per cooldown window for the given sessionStorage key,
   * either to pick up a new build's lazy chunks or to reset a saturated JS module realm.
   */
  private reloadOnce(storageKey: string, cooldownMs: number): boolean {
    const view = this.document.defaultView;
    if (!view) {
      return false;
    }
    try {
      const storage = view.sessionStorage;
      if (!storage) {
        return false;
      }
      const lastReloadAt = Number(storage.getItem(storageKey));
      const now = Date.now();
      if (now - lastReloadAt < cooldownMs) {
        return false;
      }
      storage.setItem(storageKey, String(now));
    } catch (err: unknown) {
      if (err instanceof DOMException) {
        return false;
      }
      throw err;
    }
    view.location.reload();
    return true;
  }
}
